import { Dispatch } from 'redux';

import { CallApiWithNotification } from 'src/clients/ApiService';
import ClientsClient from 'src/clients/ClientsClient';
import UsersClient from 'src/clients/UsersClient';
import { ApiTags, appAPI } from 'src/services/api';
import { analyticsApi } from 'src/services/api/analyticsApi';
import { inboxApi } from 'src/services/api/inboxApi';
import { clearActiveCompanyId } from 'src/store/activeCompany/slice';
import {
  ADD_CLIENT_FAILURE,
  ADD_CLIENT_REQUEST,
  ADD_CLIENT_SUCCESS,
  ADD_COMPANY_SUCCESS,
  ADD_COMPANY_TO_CLIENTS,
  CLEAR_CLIENTS,
  Client,
  ClientFormData,
  ClientsActionTypes,
  Company,
  CompanyFormData,
  DELETE_CLIENTS_FAILED,
  DELETE_CLIENTS_REQUEST,
  DELETE_CLIENTS_SUCCESS,
  DELETE_COMPANIES_SUCCESS,
  DELETE_COMPANY_FAILED,
  DELETE_COMPANY_REQUEST,
  INVITE_CLIENT_FAILURE,
  INVITE_CLIENT_REQUEST,
  INVITE_CLIENT_SUCCESS,
  LOAD_CLIENTS_SUCCESS,
  LOAD_COMPANIES_SUCCESS,
  REMOVE_COMPANY_FROM_CLIENTS,
  RESET_UPDATED_CLIENT,
  UPDATE_ASSIGNED_CLIENTS,
  UPDATE_CLIENT_CUSTOM_FIELDS_REQUEST,
  UPDATE_CLIENT_FAILURE,
  UPDATE_CLIENT_REQUEST,
  UPDATE_CLIENTS_SUCCESS,
  UPDATE_COMPANIES_SUCCESS,
  UPDATE_COMPANY_FAILURE,
  UPDATE_COMPANY_REQUEST,
  UPDATE_COMPANY_SUCCESS,
} from 'src/store/clients/types';
import * as ClientsStoreTypes from 'src/store/clients/types';
import { addUpdate } from 'src/store/optimisticUpdates/slice';
import { AppThunkAction } from 'src/store/reduxTypes';
import * as UserStoreTypes from 'src/store/user/types';
import * as UsersStoreTypes from 'src/store/users/types';
import ApiUtils from 'src/utils/ApiUtils';
import { ensureApiError } from 'src/utils/Errors';
import { isActive } from 'src/utils/UserUtils';

export const setClientsAction =
  ({ clients }: { clients: Client[] }): AppThunkAction =>
  async (dispatch) => {
    const activeClients = clients.filter((c) => isActive(c));

    dispatch({
      type: LOAD_CLIENTS_SUCCESS,
      clients,
      activeClients,
    });
  };

export const setCompaniesAction = (companies: Company[]) => ({
  type: LOAD_COMPANIES_SUCCESS,
  payload: companies,
});

export const handleInsertedCompanies = (
  companies: Company[],
): ClientsActionTypes => ({
  type: ADD_COMPANY_SUCCESS,
  payload: companies,
});

export const addClient =
  (
    clientData: ClientFormData,
    options?: UpdateActionOptions,
  ): AppThunkAction<Promise<Client | null>> =>
  async (dispatch) => {
    const { showSuccessMessage } = options || {};
    function request() {
      return { type: ADD_CLIENT_REQUEST };
    }

    function success(payload: Client) {
      const clients: Client[] = [payload];
      return { type: ADD_CLIENT_SUCCESS, payload: clients };
    }

    function failure(error: string) {
      return { type: ADD_CLIENT_FAILURE, error };
    }

    dispatch(request());

    const result = await CallApiWithNotification<{
      createdItems: [Client, Company] | [Client];
    }>({
      executeFunction: ClientsClient.addClient,
      checkFunction: ApiUtils.IsBatchCreateResultSuccessful,
      params: clientData,
      successMessage: showSuccessMessage
        ? `${clientData && clientData.cognitoFirstName} has been created.`
        : '',
      errorMessage: `${
        clientData && clientData.cognitoFirstName
      } could not be created.`,
      dispatch,
    });

    if (result.status) {
      const client = result.data.createdItems.at(0);
      if (client) {
        dispatch(success(client as Client));
        const company = result.data.createdItems.at(1);
        if (company) {
          // the second item is a company and we need to pass array to insert company action
          dispatch(handleInsertedCompanies([company as Company]));
        }
        dispatch(analyticsApi.util.invalidateTags([ApiTags.analyticsClients]));
        return client as Client;
      }
    }
    const error = ensureApiError(result.data);
    dispatch(failure(error.message));
    return null;
  };

export const inviteClient =
  (
    userId: string,
    userFirstName: string,
    options?: Omit<UpdateActionOptions, 'isOptimisticChange'>,
  ) =>
  async (dispatch: Dispatch) => {
    const { showSuccessMessage } = options || {};

    function request() {
      return { type: INVITE_CLIENT_REQUEST };
    }

    function success(invitedUserId: string, invitedByUserId: string) {
      return { type: INVITE_CLIENT_SUCCESS, invitedUserId, invitedByUserId };
    }

    function failure(error: string) {
      return { type: INVITE_CLIENT_FAILURE, error };
    }

    dispatch(request());

    const result = await CallApiWithNotification({
      executeFunction: ClientsClient.inviteClient,
      params: userId,
      successMessage: showSuccessMessage
        ? `${userFirstName} was sent an email to activate their account.`
        : '',
      errorMessage: `We could not invite ${userFirstName}.`,
      dispatch,
    });

    if (result.status) {
      dispatch(success(userId, result.data.fields.invitedBy));
      return result.data;
    }
    dispatch(failure(result.data));
    return null;
  };

export const clearClientsAction = () => ({
  type: CLEAR_CLIENTS,
});

export const handleInsertedClients =
  (clients: Client[], isWsEvent?: boolean): AppThunkAction =>
  async (dispatch, getState) => {
    const {
      user: { isClient },
    } = getState();

    if (isClient && clients && clients.length > 0) {
      dispatch({
        type: UsersStoreTypes.ADD_USER_SUCCESS,
        payload: {
          user: clients.at(0),
          isClient,
        },
      });
    }

    dispatch({
      type: ADD_CLIENT_SUCCESS,
      payload: clients,
      isWsEvent,
    });
  };

export const handleModifiedClients =
  (clients: Client[], isWsEvent: boolean): AppThunkAction =>
  async (dispatch, getState) => {
    const { companies } = getState().clients;
    const authenticatedUserID = getState().user.data?.userId || '';

    const authedUserInModifiedUsers = clients.find(
      (c) => c.id === authenticatedUserID,
    );
    if (authedUserInModifiedUsers) {
      dispatch({
        type: UserStoreTypes.UPDATED_USER_ATTRIBUTE,
        attributes: authedUserInModifiedUsers.fields,
      });
    }
    dispatch({
      type: UPDATE_ASSIGNED_CLIENTS,
      payload: {
        assignedCompanies: companies.map((c) => c.id),
        clients,
      },
    });

    dispatch({
      type: UPDATE_CLIENTS_SUCCESS,
      payload: clients,
      isWsEvent,
    });
  };

export const handleModifiedCompanies = (
  companies: Company[],
  isWsEvent = false,
) => ({
  type: UPDATE_COMPANIES_SUCCESS,
  payload: companies,
  isWsEvent,
});

export const resetUpdatedClient = () => ({
  type: RESET_UPDATED_CLIENT,
});

export const handleDeletedClients =
  (clients: Client[], isWsEvent?: boolean): AppThunkAction =>
  (dispatch) => {
    const deletedClientIds = clients.map((c) => c.id);
    // filter out any notifications from the inbox that are related to the deleted user
    dispatch(
      inboxApi.util.updateQueryData(
        'getInboxNotifications',
        undefined,
        (draft) => {
          return draft.filter((notification) => {
            return !deletedClientIds.includes(notification.senderId);
          });
        },
      ),
    );

    dispatch({
      type: DELETE_CLIENTS_SUCCESS,
      payload: deletedClientIds,
      isWsEvent,
    });
  };

export const handleDeletedCompanies =
  (companies: Company[]): AppThunkAction =>
  (dispatch, getState) => {
    const { activeCompanyId } = getState().activeCompany;
    const companyIds = companies.map((c) => c.id);
    if (activeCompanyId && companyIds.includes(activeCompanyId)) {
      dispatch(clearActiveCompanyId());
    }
    dispatch({
      type: DELETE_COMPANIES_SUCCESS,
      payload: companyIds,
    });
  };

export const deleteClient =
  (user: Client): AppThunkAction =>
  async (dispatch) => {
    function request() {
      return { type: DELETE_CLIENTS_REQUEST };
    }

    function success(payload: string[]) {
      return { type: DELETE_CLIENTS_SUCCESS, payload };
    }

    function failure(error: string) {
      return { type: DELETE_CLIENTS_FAILED, error };
    }
    dispatch(request());
    // optimistically delete client
    dispatch(success([user.id]));

    const result = await CallApiWithNotification({
      executeFunction: ClientsClient.deleteUser,
      checkFunction: ApiUtils.IsBatchUpdateResultSuccessful,
      params: user,
      successMessage: `${
        user ? `${user.fields.givenName} ${user.fields.familyName}` : ''
      } has been deleted.`,
      errorMessage: `${
        user && user.owner && user.fields.givenName
      } could not be deleted.`,
      dispatch,
    });

    if (result.status) {
      const userIDs: string[] = [user.id];
      dispatch(success(userIDs));
      dispatch(analyticsApi.util.invalidateTags([ApiTags.analyticsClients]));
      // Drop stale individual file channels from getCompanyFileChannels cache.
      dispatch(appAPI.util.invalidateTags([ApiTags.fileChannels]));
    } else {
      dispatch(failure(result.data));
    }
  };
export const setLastVisitedCrmTab = (tab: string) => ({
  type: ClientsStoreTypes.SET_CRM_LAST_VISITED_TAB,
  payload: tab,
});

export const deleteCompany =
  (company: Company): AppThunkAction =>
  async (dispatch) => {
    function request() {
      return { type: DELETE_COMPANY_REQUEST };
    }

    function success(payload: string) {
      return { type: DELETE_COMPANIES_SUCCESS, payload: [payload] };
    }

    function failure(error: string) {
      return { type: DELETE_COMPANY_FAILED, error };
    }

    dispatch(request());
    // optimistically delete company
    dispatch(success(company.id));

    const result = await CallApiWithNotification({
      executeFunction: ClientsClient.deleteCompany,
      checkFunction: ApiUtils.IsBatchUpdateResultSuccessful,
      params: company,
      successMessage: `${
        company && company.fields && company.fields.name
      } has been deleted.`,
      errorMessage: `${
        company && company.fields && company.fields.name
      } could not be deleted.`,
      dispatch,
    });

    if (result.status) {
      dispatch(success(company.id));
    } else {
      dispatch(failure(result.data));
    }
  };

/**
 * handle udpating company with calling api and snack bar notification
 * @param clientData udpated company data
 * @param successMessage success message for snackbar notification
 * @param errorMessage error message for snackbar notification
 * @param isOptimisticStateChange - optimistically change state before waiting response
 */

export const updateCompanyAction =
  (
    clientData: CompanyFormData,
    successMessage?: string,
    errorMessage?: string,
    isOptimisticStateChange?: boolean,
  ): AppThunkAction =>
  async (dispatch) => {
    function request() {
      return { type: UPDATE_COMPANY_REQUEST };
    }

    function success(payload: CompanyFormData) {
      return { type: UPDATE_COMPANY_SUCCESS, payload };
    }

    function failure(error: string) {
      return { type: UPDATE_COMPANY_FAILURE, error };
    }

    dispatch(request());

    if (isOptimisticStateChange) {
      dispatch(success(clientData));
    }
    const result = await CallApiWithNotification({
      executeFunction: ClientsClient.updateCompany,
      checkFunction: ApiUtils.IsBatchUpdateResultSuccessful,
      params: clientData,
      successMessage: successMessage || '',
      errorMessage: errorMessage || '',
      dispatch,
    });

    if (result.status) {
      if (!isOptimisticStateChange) {
        dispatch(success(clientData));
      }
    } else {
      dispatch(failure(result.data));
    }
  };

interface UpdateActionOptions {
  showSuccessMessage?: boolean;
  isOptimisticChange?: boolean;
}

/**
 * call updateCompanyAction with udpated company data and snackbar notification messages
 * @param clientData
 * @param options - Object type UpdateCompanyOptions. UpdateCompanyOptions.showSuccessMessage - show success snackbar or no
 * UpdateCompanyOptions.isOptimisticChange - optimistically change state before waiting response
 */
export const updateCompany =
  (
    clientData: CompanyFormData,
    options?: UpdateActionOptions,
  ): AppThunkAction =>
  async (dispatch) => {
    const { showSuccessMessage = true, isOptimisticChange } = options || {};
    const successMessage = showSuccessMessage
      ? 'Your changes have been saved.'
      : '';
    await dispatch(
      updateCompanyAction(
        clientData,
        successMessage,
        'We were unable to save the changes.',
        isOptimisticChange,
      ),
    );
  };

/**
 * updates the stripe customer id of the client company
 * calls updateCompanyAction without snackbar notifications because
 * this is triggered during adding payment method action
 * @param customerId stripe customer id
 */

export const updateCompanyCustomer =
  (
    recipientId: string,
    customerId: string,
    companyId?: string,
  ): AppThunkAction =>
  async (dispatch, getState) => {
    const {
      user: { isClient },
      clients,
      activeCompany: { activeCompanyId },
    } = getState();
    const { companies, activeClients } = clients;

    let clientCompany = null;
    // if the current user is a client then we should access the companyID
    // from the current user and use that to update company customer
    if (isClient) {
      if (activeCompanyId) {
        clientCompany = companies.find(
          (company) => company.id === activeCompanyId,
        );
      }
    } else if (companyId) {
      clientCompany = companies.find((c) => c.id === companyId);
    } else {
      const fetchedClient = activeClients.find((c) => c.id === recipientId);
      if (fetchedClient) {
        clientCompany = companies.find(
          (comp) =>
            comp.id === fetchedClient.fields.companyId ||
            fetchedClient.fields.companyIds?.includes(comp.id),
        );
      }
    }

    if (clientCompany) {
      const clientData = {
        companyName: clientCompany.fields.name,
        companyId: clientCompany.id,
        isPlaceholder: clientCompany.fields.isPlaceholder,
        customerId,
      };
      await dispatch(updateCompanyAction(clientData));
    }
  };

export const updateClientUser =
  (clientData: ClientFormData, options?: UpdateActionOptions): AppThunkAction =>
  async (dispatch, getState) => {
    const { companies } = getState().clients;

    const { showSuccessMessage, isOptimisticChange } = options || {};
    function request() {
      return {
        type: UPDATE_CLIENT_REQUEST,
        updatingClientId: clientData.userId,
      };
    }

    function success(
      client: Partial<Client>,
      createdCompany: Partial<Company> | undefined,
    ) {
      const clients = [client];
      dispatch({
        type: UPDATE_CLIENTS_SUCCESS,
        payload: clients,
      });
      const allAssignedCompanyIDs: string[] = companies.map((c) => c.id);
      if (createdCompany && createdCompany.id) {
        allAssignedCompanyIDs.push(createdCompany.id);
      }
      return {
        type: UPDATE_ASSIGNED_CLIENTS,
        payload: {
          assignedCompanies: allAssignedCompanyIDs,
          clients,
        },
      };
    }

    function failure(error: string) {
      return {
        type: UPDATE_CLIENT_FAILURE,
        error,
        // should pass the failed updating client id
        updatingClientId: clientData.userId,
      };
    }
    dispatch(request());

    const updatingClient: Partial<Client> = {
      id: clientData.userId,
      updatedDate: new Date().toISOString(),
      fields: {
        companyId: clientData.companyId,
        companyName: clientData.companyName,
        customFields: clientData.customFields,
        mfaPreference: clientData.mfaPreference,
        notificationsMuted: clientData.notificationsMuted,
      } as any,
      additionalFields: clientData.additionalFields,
    };

    if (isOptimisticChange) {
      // Track the optimistic update for WebSocket deduplication
      if (clientData.userId) {
        dispatch(
          addUpdate({ entityType: 'CLIENT_USER', itemId: clientData.userId }),
        );
      }
      // in the event that we are clearing the companyID from the client we cannot know the placeholder company's id yet
      dispatch(success(updatingClient, undefined));
    }

    const result = await CallApiWithNotification({
      executeFunction: UsersClient.updateClientUser,
      checkFunction: ApiUtils.IsBatchUpdateResultSuccessful,
      params: clientData,
      successMessage: showSuccessMessage
        ? `${clientData && clientData.cognitoFirstName} was updated.`
        : '',
      errorMessage: `${
        clientData && clientData.cognitoFirstName
      } could not be updated.`,
      dispatch,
    });
    if (isOptimisticChange) {
      return;
    }
    if (result.status) {
      const [client, company] = result.data.createdItems;
      dispatch(success(client, company));
      if (company) {
        dispatch(handleInsertedCompanies([company]));
      }
    } else {
      dispatch(failure(result.data));
    }
  };

export const addCompanyToClients = (clientIds: string[], companyId: string) => {
  return {
    type: ADD_COMPANY_TO_CLIENTS,
    payload: {
      clientIds,
      companyId,
    },
  };
};

export const removeCompanyFromClients = (
  clientIds: string[],
  companyId: string,
) => {
  return {
    type: REMOVE_COMPANY_FROM_CLIENTS,
    payload: {
      clientIds,
      companyId,
    },
  };
};

export const startUpdateCustomFieldsAction = () => ({
  type: UPDATE_CLIENT_CUSTOM_FIELDS_REQUEST,
});
